/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/hooks/useEventConfig.ts
 *
 * Rôle :
 *   Hook de lecture de la configuration de l'ÉVÉNEMENT ACTIF
 *   (fondation multi-événement). Lit `/api/me/event-config.php` et
 *   expose l'identité (nom, accroche, dates, lieu) + le branding
 *   (palette, logos, wording) de l'événement servi par défaut.
 *
 *   ZÉRO RÉGRESSION TNE : `placeholderData` = identité TNE 2026 codée
 *   en dur ⇒ même si l'endpoint est absent / offline / V25 pas
 *   appliquée, l'app affiche TNE 2026 et le thème reste celui compilé
 *   (`tokens.css`, fallback hex = palette TNE Vosges). Le branding
 *   serveur n'est appliqué QUE s'il est présent et valide
 *   (cf. `EventThemeApplier`).
 *
 * Réutilisable : 100 % — c'est la brique d'« événement courant » qui
 *   rendra l'app multi-événement (TNE, NOLAS, autres salons DRANE).
 *
 * Voir : api/me/event-config.php, components/layout/EventThemeApplier.tsx,
 *        app/docs/MULTI-EVENT-ARCHITECTURE.md.
 *
 * @author Mika
 * @since  2026-05-16 (fondation multi-événement)
 */

import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/api/client';

/** Palette de marque — clés alignées sur les `var(--tne-color-*)`. */
export interface EventBrandingPalette {
  readonly primary?: string;
  readonly accent?: string;
  readonly orange?: string;
  readonly cream?: string;
  readonly 'text-strong'?: string;
  readonly 'on-primary'?: string;
}

export interface EventBranding {
  readonly palette?: EventBrandingPalette;
  readonly logos?: Readonly<Record<string, string>>;
  readonly wording?: Readonly<Record<string, string>>;
}

export interface EventConfig {
  readonly slug: string;
  readonly name: string;
  /**
   * LOT 7 — Type d'événement (nom commun) : « salon » (défaut), « seminaire »,
   * « portes-ouvertes », « symposium », « cycle-conferences », « biennale ».
   * Source du token d'identité `{eventType}` (cf. formatEventType.ts,
   * EventIdentityProvider). Optionnel dans la charge JSON : une base sans la
   * colonne V82 (déploiement progressif) renvoie `undefined` → repli « salon ».
   */
  readonly type?: string;
  readonly tagline: string | null;
  readonly starts_at: string | null;
  readonly ends_at: string | null;
  readonly timezone: string;
  readonly venue_name: string | null;
  readonly venue_address: string | null;
  readonly organizer: string | null;
  readonly status: string;
  readonly branding: EventBranding | null;
}

interface EventConfigResponse {
  readonly ok: true;
  readonly event: EventConfig | null;
}

export const EVENT_CONFIG_QUERY_KEY = ['me', 'event-config'] as const;

/**
 * Identité TNE 2026 par défaut — utilisée tant que le serveur n'a pas
 * répondu (placeholder) OU s'il répond `event:null`. `branding:null`
 * ⇒ aucun override CSS ⇒ thème compilé conservé (TNE Vosges).
 */
export const DEFAULT_EVENT_CONFIG: EventConfig = {
  slug: 'nolas-2026',
  name: 'NOLAS 2026',
  type: 'salon', // LOT 7 — rétrocompat : NOLAS est un salon.
  tagline: 'Salon du Recrutement et de l’Emploi — Votre avenir commence ICI !',
  starts_at: '2026-10-09 08:30:00',
  ends_at: '2026-10-09 13:00:00',
  timezone: 'Europe/Paris',
  venue_name: 'SPOT de Thionville',
  venue_address: 'Place de la Liberté, Thionville',
  organizer: 'NOLAS — Salon du Recrutement et de l’Emploi',
  status: 'active',
  branding: null,
};

/**
 * Renvoie la config de l'événement actif. `event` n'est jamais null
 * côté consommateur : on retombe sur {@link DEFAULT_EVENT_CONFIG}.
 */
export function useEventConfig(): {
  readonly event: EventConfig;
  readonly isLoading: boolean;
} {
  const query = useQuery<EventConfigResponse, Error>({
    queryKey: EVENT_CONFIG_QUERY_KEY,
    queryFn: () => apiRequest<EventConfigResponse>('me/event-config.php'),
    // Cohérent Cache-Control serveur (120 s) — le branding bouge peu.
    staleTime: 120_000,
    gcTime: 600_000,
    // 1 retry suffit ; au-delà on garde le défaut (offline-first §18).
    retry: 1,
    refetchOnWindowFocus: false,
    placeholderData: { ok: true, event: DEFAULT_EVENT_CONFIG },
  });

  const event = query.data?.event ?? DEFAULT_EVENT_CONFIG;
  return { event, isLoading: query.isLoading };
}
